// @ts-nocheck

/* eslint-disable no-use-before-define */

import React, { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { TopCompetitions as TopCompetitionsIcon } from '../../assets/svg/sports-menu';
import { Target } from '../../client';
import { SPORTS_MENU_LIST_ITEM_BORDER_RADIUS, SPORTS_MENU_LIST_ITEM_HEIGHT } from '../../constants/ui/sportsMenu';
import { getAppDOMElement } from '../../DOMElements';
import useSportsMenuScrollContext from '../../hooks/useSportsMenuScrollContext';
import { __ } from '../../i18n/l10n';
import type { SportsMenuItem } from '../../states/sportsmenu';
import theme from '../../styled-components/dark';
import { NewTag } from '../../styled-components/shared';
import { SportsMenuTitleLabel } from '../../styled-components/shared';
import ExpandArrow from '../ExpandArrow';
import FlagIcon from '../FlagIcon';
import ListItem, { ListWrapper } from '../ListItem';
import SportIcon from '../SportIcon';

const Wrapper = styled.div``;

const Label = styled.span`
  margin-left: 0.5em;
  padding-right: 0.3em;
  height: 100%;
  flex: 1;
  display: flex;
  align-items: center;

  margin-left: ${({ $margin }) => ($margin ? '2.3em' : undefined)};

  border-top: ${({ $index }) => ($index ? `1px solid ${theme.SportsMenuListItem.separatorColor}` : undefined)};
`;

const IconWrapper = styled.div`
  margin-left: 0.2em;
  display: flex;
  align-items: center;
`;

const FlagIconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0.5em;
  font-size: 1em;
`;

const StyledTopCompetitionsIcon = styled(TopCompetitionsIcon)`
  width: 1.3em;
  height: 1.3em;
  margin: 0 0.5em;
`;

const StyledSportIcon = styled(SportIcon)`
  width: 2em;
  height: 2em;
`;

const StyledArrow = styled(ExpandArrow)`
  position: absolute;
  right: 0.7em;
  height: 1em;
  width: 1em;
  transition: transform 150ms ease-in-out;
  transform: rotate(90deg);

  & .sports-arrow-inner-group {
    stroke: ${theme.SportsMenuListItem.arrowColor};
  }
`;

const StyledFlagIcon = styled(FlagIcon)``;

const StyledListWrapper = styled(ListWrapper)`
  background-color: ${({ $background }) => ($background ? theme.SportsMenuListItem.backgroundColor : 'transparent')};
  border-radius: ${SPORTS_MENU_LIST_ITEM_BORDER_RADIUS}px;
`;

const StyledListItem = styled(ListItem)`
  background-image: none;
  height: ${SPORTS_MENU_LIST_ITEM_HEIGHT}em;
  border-top: none;
  padding: 0 0.5em;
  font-size: 1em;

  ${Target.__MOBILE__
    ? css`
        letter-spacing: normal;
      `
    : undefined}

  &:hover {
    ${Target.__MOBILE__
      ? undefined
      : css`
          background-color: ${theme.SportsMenuListItem.hoverColor};
          filter: brightness(110%);
          box-shadow: none;
        `}
  }

  &:active {
    background-color: ${theme.SportsMenuListItem.active.backgroundColor};
    filter: brightness(100%);
  }

  padding-left: ${({ $padding }) => ($padding ? `${$padding * 2}em` : undefined)};
`;

const StyledTitleLabel = styled(SportsMenuTitleLabel)`
  ${({ $depth }) => css`
    padding-left: ${$depth * 2}em;
  `};
`;

const StyledNewTag = styled(NewTag)`
  position: absolute;
  right: 3em;
`;

type SportsMenuTopCompetitionsProps = {
  items: Array<SportsMenuItem> | null;
  padHeader?: boolean;
  showSportIcon?: boolean;
  className?: string;
  depth?: number;
};

const SportsMenuTopCompetitions = ({
  items,
  padHeader = false,
  showSportIcon = false,
  className = '',
  depth = 0,
}: SportsMenuTopCompetitionsProps) => {
  const { setScrollPositions } = useSportsMenuScrollContext();
  const location = useLocation();
  const scrollElement = getAppDOMElement();

  const saveScrollPosition = useCallback(() => {
    if (Target.__MOBILE__ && setScrollPositions) {
      setScrollPositions((prev) => ({ ...prev, [location.pathname]: scrollElement.scrollTop }));
    }
  }, [location, scrollElement, setScrollPositions]);

  const renderItem = useCallback(
    (item, idx) => {
      const result = [];

      if (item.sportId && showSportIcon) {
        result.push(
          <IconWrapper key={`sport-${idx}`}>
            <StyledSportIcon sportId={item.sportId} />
          </IconWrapper>,
        );
      }
      if (item.flagId) {
        result.push(
          <FlagIconWrapper key={`flag-${idx}`}>
            <StyledFlagIcon flagId={item.flagId} />
          </FlagIconWrapper>,
        );
      }

      result.push(
        <Label key={`label-${idx}`} $index={idx} $margin={!!depth && !item.sportId && !item.flagId}>
          {item.label}
        </Label>,
      );
      if (item.showNewTag) {
        result.push(<StyledNewTag key={`new-tag-${idx}`}>New</StyledNewTag>);
      }
      return (
        <StyledListItem
          key={`k-${idx}`}
          $pad={padHeader}
          to={item.to}
          $padding={depth}
          onClick={saveScrollPosition}
          hoverColor={theme.ListItem.Row.activeBackgroundColor}
        >
          {result}
          <StyledArrow className="see-all-btn" />
        </StyledListItem>
      );
    },
    [showSportIcon, padHeader, depth, saveScrollPosition],
  );

  return (
    <Wrapper className={className}>
      <StyledTitleLabel $pad={padHeader}>
        <StyledTopCompetitionsIcon />
        {__('Top competitions')}
      </StyledTitleLabel>
      <StyledListWrapper $background={depth === 0} $border={!padHeader}>
        {items ? items.map(renderItem) : null}
      </StyledListWrapper>
    </Wrapper>
  );
};

export default React.memo<SportsMenuTopCompetitionsProps>(SportsMenuTopCompetitions);
